import React, { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../jss_setup";
import { useContext } from "react";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { useBookingProcess } from "../../../hooks/booking";
import Stepper from "../../common/Stepper";
import { Button } from "../../common/Button";
import { useSelector } from "react-redux";
import { createBookPayload, getInitialValues, validateFn } from "./helpers";
import {
  useGetTraQueryParams,
  useTrainsGetSessionMeta,
} from "../../../hooks/trains";
import { useHistory } from "react-router-dom";
import { Prebook } from "./Prebook";
import { Formik } from "formik";
import {
  toLowerCaseArrayValues,
  validationSchema,
} from "./TravellerForms/helpers";
import { ActionBtnsLoader, BackBtn } from "./conponents";
import TravellerForms from "./TravellerForms";
import { Icon } from "@iconify/react";
import { useTrainBookingProcess } from "../../../hooks/booking/train";
import { toast } from "react-toastify";
import { useApoAuth } from "../../../hooks";
import { website_api_key } from "../../../api";

const styles = createUseStyles({
  TrainsBookingProcess: (props) => ({
    position: "absolute",
    zIndex: 9999,
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "white",
    display: "grid",
    ..._.get(props, "customStyles.TrainsBookingProcess", {}),
  }),
  TrainsBookingProcessContr: (props) => ({
    display: "grid",
    gridTemplateRows: props.step === 1 ? "1fr max-content " : "max-content 1fr",

    gridGap: jssVariables.normal_gap,
    padding: [
      jssVariables.double_gap,
      jssVariables.spacing.center_padding_mobile_medium[1],
    ],
    paddingBottom: `calc(${jssVariables.navbar_height} + ${jssVariables.normal_gap})`,
    minHeight: `calc(100vh - ${jssVariables.navbar_height})`,
    ..._.get(props, "customStyles.TrainsBookingProcessContr", {}),
  }),
  stepper: (props) => ({
    ..._.get(props, "customStyles.stepper", {}),
  }),
  travelInfoContr: (props) => ({
    display: "grid",
    gridTemplateRows: "1fr max-content",
    gridGap: jssVariables.double_gap,
    ..._.get(props, "customStyles.travellersFormsContr", {}),
  }),
  content: (props) => ({
    width: "90vw",
    ..._.get(props, "customStyles.content", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.actions", {}),
  }),
  onPaymentLoader: (props) => ({
    display: "grid",
    justifyItems: "center",
    alignContent: "center",
    gridGap: jssVariables.double_gap,
    ..._.get(props, "customStyles.onPaymentLoader", {}),
  }),
  onPaymentLoaderIcon: (props) => ({
    fontSize: "xxx-large",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.onPaymentLoaderIcon", {}),
  }),
  onPaymentLoaderText: (props) => ({
    color: jssVariables.colors.base_color,
    textAlign: "center",
    ..._.get(props, "customStyles.onPaymentLoaderText", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    TrainsBookingProcess: (props) => ({
      ..._.get(props, "customStyles.TrainsBookingProcess.smallscreen", {}),
    }),
    TrainsBookingProcessContr: (props) => ({
      minWidth: "45rem",
      maxWidth: "60rem",
      justifySelf: "center",
      height: "100vh",
      paddingBottom: jssVariables.double_gap,
      ..._.get(props, "customStyles.TrainsBookingProcessContr.smallscreen", {}),
    }),
    stepper: (props) => ({
      ..._.get(props, "customStyles.stepper.smallscreen", {}),
    }),
    content: (props) => ({
      width: "100%",
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    actions: (props) => ({
      ..._.get(props, "customStyles.actions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    TrainsBookingProcess: (props) => ({
      ..._.get(props, "customStyles.TrainsBookingProcess.bigscreen", {}),
    }),
    TrainsBookingProcessContr: (props) => ({
      maxWidth: "60rem",
      ..._.get(props, "customStyles.TrainsBookingProcessContr.bigscreen", {}),
    }),
    stepper: (props) => ({
      ..._.get(props, "customStyles.stepper.bigscreen", {}),
    }),
    content: (props) => ({
      ..._.get(props, "customStyles.content.bigscreen", {}),
    }),
    actions: (props) => ({
      ..._.get(props, "customStyles.actions.bigscreen", {}),
    }),
  },
});
const TrainsBookingProcess = ({ page_session_id }) => {
  const [prebookData, setPrebookData] = useState({});
  const [agreed, setAgreed] = useState(false);

  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const history = useHistory();

  const { source_entity } = useApoAuth();

  const { selectionData } = useGetTraQueryParams();
  const { search_session_id, market_type } = useTrainsGetSessionMeta({
    page_session_id,
  });

  const { loading, setLoading, onBook } = useTrainBookingProcess({
    page_session_id,
    market_type,
  });
  const { step, onIncreaseStep, onDecreaseStep } = useBookingProcess();

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "TrainsBookingProcess",
    }),
    noGapAfterBtns: step === 0,
  });

  const { adults, youths, seniors, adults_ages, youths_ages, seniors_ages } =
    useSelector((state) => {
      return {
        adults: state.TrainSearchForm.adults,
        youths: state.TrainSearchForm.youths,
        seniors: state.TrainSearchForm.seniors,
        adults_ages: state.TrainSearchForm.adults_ages,
        youths_ages: state.TrainSearchForm.youths_ages,
        seniors_ages: state.TrainSearchForm.seniors_ages,
      };
    });

  const requiredFieldsInitial = _.get(
    prebookData,
    "booking_items.0.traveler_information_required",
    {}
  );

  const leadDefaultReqInfo = toLowerCaseArrayValues(
    requiredFieldsInitial?.lead_information_required
  );

  const defaultReqFields = [
    ...toLowerCaseArrayValues(requiredFieldsInitial?.default_information),
  ];

  const requiredFields = { defaultReqFields, leadDefaultReqInfo };
  const initialValues = getInitialValues({
    adults,
    youths,
    seniors,
    adults_ages,
    youths_ages,
    defaultReqFields,
    seniors_ages,
    travellersWithIds: prebookData?.booking_items?.[0]?.travelers ?? [],
  });

  function prevStep() {
    if (step > 0) {
      onDecreaseStep();
      return;
    }
    if (step === 0) {
      history.goBack();
      return;
    }
  }
  const backBtnProps = { step, t, prevStep };

  return (
    <div className={classes.TrainsBookingProcess}>
      <div className={classes.TrainsBookingProcessContr}>
        <div className={classes.stepper}>
          <Stepper
            steps={[t("prebook"), t("travelInfo"), t("payment")]}
            current={step === 1 ? 1 : step}
          />
        </div>
        {step === 0 ? (
          <React.Fragment>
            <Prebook
              loading={loading}
              setLoading={setLoading}
              page_session_id={page_session_id}
              prebookData={prebookData}
              setPrebookData={setPrebookData}
              agreed={agreed}
              setAgreed={setAgreed}
            />
          </React.Fragment>
        ) : step === 1 ? (
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            initialValues={initialValues}
            validate={async (values) => {
              return await validateFn({
                values,
                t,
                adults,
                seniors,
                youths,
                defaultReqFields: requiredFields.defaultReqFields,
              });
            }}
            onSubmit={(values) => {
              const bookPayload = createBookPayload({
                search_session_id,
                api_uid: prebookData.api_uid,
                prebookData,
                selectionData,
                values,
                market_type,
              });
              onBook(bookPayload);
              setLoading(true);
              onIncreaseStep();
            }}>
            <TravellerForms
              prebookData={prebookData}
              backBtnProps={backBtnProps}
              requiredFields={requiredFields}
            />
          </Formik>
        ) : step === 2 ? (
          <div className={classes.onPaymentLoader}>
            <Icon
              icon="line-md:loading-twotone-loop"
              className={classes.onPaymentLoaderIcon}
            />
            <h4 className={classes.onPaymentLoaderText}>
              {t("waitWhileProcessingPayment")}
            </h4>
          </div>
        ) : null}
        {step !== 1 && (
          <div className={classes.actions}>
            {loading ? (
              <ActionBtnsLoader />
            ) : (
              <React.Fragment>
                <BackBtn backBtnProps={backBtnProps} />
                <Button
                  id="trains_next_btn"
                  text={step < 2 ? t("next") : t("payment")}
                  bold={true}
                  size="large"
                  onClick={() => {
                    if (step === 0 && !agreed) {
                      toast.warning(t("pleaseCheckTheBox"));
                      return;
                    }
                    onIncreaseStep();
                  }}
                />
              </React.Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
TrainsBookingProcess.defaultProps = {
  page_session_id: "",
};
TrainsBookingProcess.propTypes = {
  page_session_id: PropTypes.string,
};
export default TrainsBookingProcess;
